import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { BrandedAlertModal } from '../BrandedAlertModal';
import type { BrandedAction, BrandedIllustrationVariant } from '../BrandedAlertModal/BrandedAlertModal';
import {
  MembershipCooldownIcon,
  MembershipCourseIcon,
  MembershipHoldIcon,
  MembershipLimitIcon,
  MembershipRestrictionIcon,
} from '../membershipModalIcons';
import { openMembershipPath } from 'utils/membership';
import type { MembershipRestrictionDialogCopy } from 'utils/membership';

export type MembershipDialogCopy = MembershipRestrictionDialogCopy;

export interface MembershipRestrictionModalProps {
  isOpen: boolean;
  copy: MembershipDialogCopy | null;
  onClose: () => void;
}

type ModalPresentation = {
  headerLabel: string;
  Icon: React.FC;
  illustrationVariant: BrandedIllustrationVariant;
};

const PRESENTATION_BY_CODE: Record<string, ModalPresentation> = {
  CANCELLATION_COOLDOWN: {
    headerLabel: 'Shift Booking',
    Icon: MembershipCooldownIcon,
    illustrationVariant: 'info',
  },
  COURSE_REQUIRED: {
    headerLabel: 'Account Status',
    Icon: MembershipCourseIcon,
    illustrationVariant: 'info',
  },
  ON_A_BREAK: {
    headerLabel: 'Account Status',
    Icon: MembershipHoldIcon,
    illustrationVariant: 'info',
  },
  MAX_BOOKED_REACHED: {
    headerLabel: 'Shift Booking',
    Icon: MembershipLimitIcon,
    illustrationVariant: 'info',
  },
  DISTANCE_EXCEEDED: {
    headerLabel: 'Shift Booking',
    Icon: MembershipRestrictionIcon,
    illustrationVariant: 'info',
  },
  LEAD_TIME_EXCEEDED: {
    headerLabel: 'Shift Booking',
    Icon: MembershipRestrictionIcon,
    illustrationVariant: 'info',
  },
  NOT_A_FIT: {
    headerLabel: 'Account Status',
    Icon: MembershipRestrictionIcon,
    illustrationVariant: 'danger',
  },
};

const DEFAULT_PRESENTATION: ModalPresentation = {
  headerLabel: 'Shift unavailable',
  Icon: MembershipRestrictionIcon,
  illustrationVariant: 'info',
};

function getPresentation(code: string): ModalPresentation {
  return PRESENTATION_BY_CODE[code] ?? DEFAULT_PRESENTATION;
}

/** T2-6173: house-style modal for membership API errors (header bar + illustration + title). */
const MembershipRestrictionModal: React.FC<MembershipRestrictionModalProps> = ({
  isOpen,
  copy,
  onClose,
}) => {
  const history = useHistory();

  const handlePrimary = useCallback(() => {
    onClose();
    if (copy?.ctaPath) {
      openMembershipPath(history, copy.ctaPath);
    }
  }, [copy, history, onClose]);

  const presentation = useMemo(
    () => (copy ? getPresentation(copy.code) : DEFAULT_PRESENTATION),
    [copy],
  );

  if (!copy) {
    return null;
  }

  const { Icon, headerLabel, illustrationVariant } = presentation;
  const icon = <Icon />;

  const actions: BrandedAction[] = [
    { label: copy.ctaLabel || 'Ok', variant: 'primary', onClick: handlePrimary },
    { label: 'Close', variant: 'secondary', onClick: onClose },
  ];

  return (
    <BrandedAlertModal
      isOpen={isOpen}
      onClose={onClose}
      headerLabel={headerLabel}
      headerIcon={icon}
      illustration={icon}
      illustrationVariant={illustrationVariant}
      title={copy.title}
      ariaLabel={copy.title || 'Notice'}
      actions={actions}
    >
      {copy.content}
    </BrandedAlertModal>
  );
};

export default MembershipRestrictionModal;
